import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getReasonComments, addReasonComment } from '../../../firebase/solutionReasonCommentsApi.js';
import { fetchUsersByIds } from '../../slices/usersCacheSlice.js';

// Helper to convert Firestore timestamps (or JS dates/strings) to millis for sorting
const toMillis = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === 'string') return new Date(ts).getTime() || 0;
    if (typeof ts === 'number') return ts;
  } catch {}
  return 0;
};

// Async thunks
export const fetchReasonComments = createAsyncThunk(
  'solutionView/reasonComments/fetch',
  async (reasonId, { dispatch }) => {
    const res = await getReasonComments(reasonId, { pageSize: 20, fetchPolicy: 'cache-first' });
    try {
      const ids = Array.from(new Set((res?.comments || []).map(c => c.userId).filter(Boolean)));
      if (ids.length > 0) {
        dispatch(fetchUsersByIds({ userIds: ids }));
      }
    } catch (_) {}
    return res;
  }
);

export const postReasonComment = createAsyncThunk(
  'solutionView/reasonComments/add',
  async ({ reasonId, userId, userName, userProfession, text }) => {
    return await addReasonComment({ reasonId, userId, userName, userProfession, text });
  }
);

const reasonCommentsSlice = createSlice({
  name: 'solutionViewReasonComments',
  initialState: {
    commentsByReasonId: {} // { [reasonId]: { comments: [], loading: false, error: null } }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReasonComments.pending, (state, action) => {
        const id = action.meta.arg;
        state.commentsByReasonId[id] = state.commentsByReasonId[id] || { comments: [] };
        state.commentsByReasonId[id].loading = true;
        state.commentsByReasonId[id].error = null;
      })
      .addCase(fetchReasonComments.fulfilled, (state, action) => {
        const { reasonId, comments } = action.payload;
        const sorted = (comments || []).slice().sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp));
        state.commentsByReasonId[reasonId] = {
          comments: sorted,
          loading: false,
          error: null
        };
      })
      .addCase(fetchReasonComments.rejected, (state, action) => {
        const id = action.meta.arg;
        state.commentsByReasonId[id] = state.commentsByReasonId[id] || { comments: [] };
        state.commentsByReasonId[id].loading = false;
        state.commentsByReasonId[id].error = action.error.message;
      })
      // Optimistic insert on pending
      .addCase(postReasonComment.pending, (state, action) => {
        const { reasonId, userId, userName, userProfession, text } = action.meta.arg || {};
        const requestId = action.meta.requestId;
        state.commentsByReasonId[reasonId] = state.commentsByReasonId[reasonId] || { comments: [] };
        state.commentsByReasonId[reasonId].comments.push({
          id: `optimistic-${requestId}`,
          clientRequestId: requestId,
          reasonId,
          parentCommentId: null, // Top-level comment
          userId,
          userName,
          userProfession,
          text,
          timestamp: new Date(),
          __optimistic: true
        });
      })
      .addCase(postReasonComment.fulfilled, (state, action) => {
        const { reasonId, comment } = action.payload;
        const requestId = action.meta.requestId;
        state.commentsByReasonId[reasonId] = state.commentsByReasonId[reasonId] || { comments: [] };
        // Replace optimistic comment if present
        const list = state.commentsByReasonId[reasonId].comments;
        const idx = list.findIndex((c) => c.clientRequestId === requestId || c.__optimistic);
        if (idx !== -1) {
          list[idx] = comment;
        } else {
          list.push(comment);
        }
      })
      .addCase(postReasonComment.rejected, (state, action) => {
        const { reasonId } = action.meta.arg || {};
        const requestId = action.meta.requestId;
        if (!reasonId) return;
        state.commentsByReasonId[reasonId] = state.commentsByReasonId[reasonId] || { comments: [] };
        const list = state.commentsByReasonId[reasonId].comments;
        const idx = list.findIndex((c) => c.clientRequestId === requestId || c.__optimistic);
        if (idx !== -1) list.splice(idx, 1);
      });
  }
});

export default reasonCommentsSlice.reducer;
